import React, { Fragment, MutableRefObject, useContext, useEffect, useRef, useState } from "react";

import { ElementState, Preview } from "@creatomate/preview";

import { ElementPlacement, ElementSource } from "chq";
import { useContextMenu } from "app/hooks";

import { timeToNumber } from "components/stories/video-studio/utils";
import { getOverlappingElements, moveOverlappedElement, movePlayhead } from "./utils";
import { VIDEO_TYPE } from "../../utils/constants";
import { useActiveElementContext } from "../context/ActiveElementContext";

import ElementContextOptions from "./context-menu/ElementContextOptions";
import ResizeHandlers from "./resize/ResizeHandlers";
import DraggableElement from "./draggable-element/DraggableElement";
import { DragContext } from "../context/DragContext";
import { TrimContext } from "../context/TrimContext";
import { CreatomateContext } from "../../contexts/VideoStudioContext";
import { useTimelineScale } from "../../contexts/TimelineScaleContext";
import { isSubtitleText, trimSubtitles, updateSubtitleSectionTimeById, updateSubtitlesSectionsPositionByRange } from "../../sidebar/tabs/subtitle/utils";
import { Mode, TRANSITION_ANIMATION_SUBTRACTION } from "../../sidebar/animation/AnimationTypes";

export const VIDEO_ELEMENT_HEIGHT = 75;
export const SMALL_ELEMENT = 50;

interface ElementProps {
  currentElement: ElementState;
  elements?: ElementState[];
  preview: Preview;
  withVideos: boolean;
}

const Element: React.FC<ElementProps> = ({
  currentElement,
  elements,
  preview,
  withVideos
}) => {
  const { data, handleUpdateData } = useContext(CreatomateContext);
  const { timelineScale } = useTimelineScale();
  const { activeElementsId, mediaVersionHistory } = data;

  const isTheOriginal = mediaVersionHistory?.length === 0;
  const elementType = currentElement.source.type;

  const barRef = useRef() as MutableRefObject<HTMLDivElement>;
  const active = activeElementsId.includes(currentElement.source.id);
  const activeElementContext = useActiveElementContext();
  const { draggingElement } = useContext(DragContext);
  const { isTrimmingStart, trimmingElement } = useContext(TrimContext);

  // gapPlacement is being used through setGapPlacement
  const [translateX, setTranslateX] = useState(0);
  const [_gapPlacement, setGapPlacement] = useState({ time: -1, duration: -1 });
  const [placement, setPlacement] = useState<ElementPlacement>({
    time: currentElement.globalTime, duration: currentElement.duration
  });
  const [trimming, setTrimming] = useState(false);
  const isMounted = useRef<boolean>(false);

  const isDraggingElement = currentElement.source.id === draggingElement?.source?.id;
  const isTrimmingElement = currentElement.source.id === trimmingElement?.source?.id;

  const onContextMenu = () => {
    if (!activeElementsId.includes(currentElement.source.id)) {
      preview.setActiveElements([currentElement.source.id]);
    }
  };

  const {
    contextMenuVisible,
    contextMenuPosition,
    contextMenuRef,
    handleContextMenu,
    handleSetContextMenuVisible
  } = useContextMenu(onContextMenu);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // the user is dragging/trimming an active element
  useEffect(
    () => {
      if (active) {
        // the user is dragging an active element
        if (activeElementContext.drag && !isDraggingElement) {
          if (barRef.current) {
            barRef.current.style.transform = `translate(${activeElementContext.transition}px, 0px)`;
          }
          const timeOffset = activeElementContext.transition / timelineScale;
          const time = Math.max(currentElement.globalTime + timeOffset, 0);
          setPlacement({ time, duration: currentElement.duration });
        } else if (activeElementContext.trim && !isTrimmingElement) {
          // the user is trimming an active element
          setPlacement({
            time: Math.max(placement.time + activeElementContext.placement.time, 0),
            duration: placement.duration + activeElementContext.placement.duration
          });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeElementContext.placement]
  );

  useEffect(() => {
    setPlacement({ time: currentElement.localTime, duration: currentElement.duration });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentElement.localTime, currentElement.duration]);

  const applyPlacementMutltipleElements = (
    updatedElements: ElementSource[], trim?: boolean, startTrim?: boolean
  ): ElementSource[] => {
    if (trim) {
      if (startTrim) {
        const timeOffset = placement.time - currentElement.globalTime;
        return updatedElements.map((element: ElementSource) => {
          if (!activeElementsId.includes(element.id)) return element;

          return {
            ...element,
            trim_start: Math.max(timeToNumber(element.trim_start) + timeOffset, 0),
            time: element.time + timeOffset,
            duration: element.duration - timeOffset
          };
        });
      }

      // trimming end time
      const durationOffset = placement.duration - currentElement.duration;
      return updatedElements.map((element: ElementSource) => {
        if (!activeElementsId.includes(element.id)) return element;

        return {
          ...element,
          duration: element.duration + durationOffset
        };
      });
    }

    // dragging multiple elements
    const timeOffset = placement.time - currentElement.globalTime;
    return updatedElements.map((element: ElementSource) => {
      if (!activeElementsId.includes(element.id)) return element;

      return {
        ...element,
        time: element.time + timeOffset
      };
    });
  };

  const applyPlacementSingleElement = (
    existingElements: ElementSource[],
    allSubtitles: ElementSource[],
    trim?: boolean,
    startTrim?: boolean,
    trackIndex?: number
  ): { newElements: ElementSource[]; newPlacement: ElementPlacement } => {
    let newPlacement = { ...placement };
    let newElements: ElementSource[] = [...existingElements];

    if (trim) {
      // This case is just when you trim the original video...
      // This works just if you trim one single element (video), because there is a race condition
      // when you delete subtitles and apply modifications to different elements at the same time
      if (elementType === VIDEO_TYPE && allSubtitles.length > 0) {
        newElements = [];
        const noSubtitlesElements = existingElements.filter((element: ElementSource) => (
          !isSubtitleText(element.name)
        ));
        newElements.push(...noSubtitlesElements);

        const trimmedSubtitles = trimSubtitles(allSubtitles, placement, currentElement.source.id);
        newElements.push(...trimmedSubtitles);
      }
      if (startTrim) {
        newElements = newElements.map((element: ElementSource) => {
          if (currentElement.source.id !== element.id) return element;

          return {
            ...element,
            trim_start: Math.max(timeToNumber(currentElement.source.trim_start)
              + (placement.time - currentElement.globalTime), 0),
            time: placement.time,
            duration: placement.duration
          };
        });
      } else {
        newElements = newElements.map((element: ElementSource) => {
          if (currentElement.source.id !== element.id) return element;

          return {
            ...element,
            duration: placement.duration
          };
        });
      }
      return { newElements, newPlacement };
    }

    // dragging single element to another track
    if (trackIndex !== undefined) {
      if (trackIndex === 0) {
        // dragging single element to the lowest track
        newElements = newElements.map(nElement => {
          if (nElement.id === currentElement.source.id) {
            return {
              ...nElement, track: 1, time: newPlacement.time, duration: newPlacement.duration };
          }

          return { ...nElement, track: nElement.track + 1 };
        });

        return { newElements, newPlacement };
      }

      const highestTrack = Math.max(...existingElements.map(el => el.track));
      if (highestTrack < trackIndex) {
        // dragging single element to the highest track
        newElements = newElements.map(nElement => {
          if (nElement.id === currentElement.source.id) {
            return {
              ...nElement,
              track: highestTrack + 1,
              time: newPlacement.time,
              duration: newPlacement.duration };
          }

          return nElement;
        });

        return { newElements, newPlacement };
      }

      // dragging single element to existing track
      const trackElements = data.elements.filter(de => de.track === trackIndex);
      const overlappingElements = getOverlappingElements(currentElement, placement, trackElements);
      const transitionOverlap = allowTransitionElement();

      if (overlappingElements && overlappingElements.length > 0 && !transitionOverlap.allow) {
        // overlapping in the new track
        const modifiedCurrentElement = trackIndex === undefined ? currentElement
          : { ...currentElement,
            track: trackIndex,
            source: {
              ...currentElement.source, track: trackIndex } };

        const modifiedNewElements = newElements.map(nElement => {
          if (nElement.id === currentElement.source.id) {
            return { ...nElement, track: trackIndex };
          }

          return nElement;
        });

        const modifiedTrackElements = trackElements;
        modifiedTrackElements.push(modifiedCurrentElement);
        const {
          updatedElements,
          newPlacement: updatedPlacement
        } = moveOverlappedElement(
          modifiedNewElements,
          modifiedCurrentElement,
          placement,
          overlappingElements,
          modifiedTrackElements);

        return { newElements: updatedElements, newPlacement: updatedPlacement };
      }

      // no overlapping in the new track
      newElements = [...newElements.map((element: ElementSource) => {
        if (element.id !== currentElement.source.id) return element;
        return {
          ...element,
          time: placement.time,
          duration: placement.duration,
          track: trackIndex
        };
      })];

      return { newElements, newPlacement };
    }
    // dragging single element on the same track
    const overlappingElements = getOverlappingElements(currentElement, placement, elements);
    const transitionOverlap = allowTransitionElement();

    if (overlappingElements && overlappingElements.length > 0 && !transitionOverlap.allow) {
      const {
        updatedElements,
        newPlacement: updatedPlacement
      } = moveOverlappedElement(
        newElements,
        currentElement,
        placement,
        overlappingElements,
        elements
      );
      newElements = [...updatedElements];
      newPlacement = { ...updatedPlacement };
    } else {
      // no overlapping on the same track
      newElements = [...newElements.map((element: ElementSource) => {
        if (element.id !== currentElement.source.id) return element;
        return {
          ...element,
          time: placement.time,
          duration: placement.duration
        };
      })];

      if (transitionOverlap.prevElement) {
        const prevEndTime = transitionOverlap.prevElement.globalTime
          + transitionOverlap.prevElement.duration;

        const transitionDuration = (prevEndTime - placement.time)
          + TRANSITION_ANIMATION_SUBTRACTION;

        const currentAnimations: any[] = (currentElement.source.animations as any[]).filter(
          animation => !(Mode.Transition in animation));

        currentAnimations.push({
          ...transitionOverlap.transition,
          duration: transitionDuration
        });

        newElements = newElements.map((element: ElementSource) => {
          if (element.id !== currentElement.source.id) return element;
          return {
            ...element,
            animations: currentAnimations
          };
        });
      }
    }
    return { newElements, newPlacement };
  };

  const applyPlacement = async (trim?: boolean, startTrim?: boolean, trackIndex?: number) => {
    const source = preview.getSource();
    const allSubtitles = source.elements.filter((element: ElementSource) => (
      isSubtitleText(element.name)
    ));

    if (elementType === VIDEO_TYPE && !isTheOriginal && allSubtitles.length === 0) {
      handleUpdateData({ blockSubtitles: true });
    }

    // build an array of elements to apply
    let newElements: ElementSource[] = [...source.elements];
    let newPlacement = { ...placement };

    if (activeElementsId.length > 1) {
      newElements = applyPlacementMutltipleElements(newElements, trim, startTrim);
    } else {
      const {
        newElements: updatedElements, newPlacement: updatedPlacement
      } = applyPlacementSingleElement(
        newElements,
        allSubtitles,
        trim,
        startTrim,
        trackIndex
      );
      newElements = [...updatedElements];
      newPlacement = { ...updatedPlacement };
    }

    if (!trim) {
      if (elementType === VIDEO_TYPE && allSubtitles.length > 0) {
        // Here we need to move all the corresponding subtitles (together with the video)
        newElements = updateSubtitlesSectionsPositionByRange(
          newElements,
          newPlacement.time - currentElement.globalTime,
          currentElement.source.id,
          currentElement.globalTime,
          currentElement.globalTime + currentElement.duration
        );
      }
    }

    if (isMounted.current) setPlacement(newPlacement);
    await movePlayhead(preview, newPlacement.time);

    if (isSubtitleText(currentElement.source.name)) {
      newElements = updateSubtitleSectionTimeById(
        newElements,
        activeElementsId,
        currentElement,
        newPlacement.time,
        newPlacement.duration
      );
    }

    if (!preview.ready) return;

    // We have to do this because if the duration was set and you trim the video,
    // the duration should change dynamically
    await preview.setSource({
      ...source,
      elements: newElements,
      duration: elementType === VIDEO_TYPE ? null : source.duration
    }, true);
  };

  // allow overlapping with the previous element if the element has a transition
  const allowTransitionElement = () => {
    const overlappingElements = getOverlappingElements(currentElement, placement, elements);
    if (overlappingElements && overlappingElements.length > 0) {
      const prevElement = overlappingElements.sort((a, b) => b.globalTime - a.globalTime)[0];
      const hasAnimations: any[] = currentElement.source.animations;
      if (hasAnimations) {
        const hasTransition = hasAnimations.filter(animation => {
          if (Mode.Transition in animation) {
            return animation.transition;
          }
          return false;
        });

        if (hasTransition.length > 0) {
          return {
            allow: prevElement.globalTime < placement.time,
            prevElement,
            transition: hasTransition[0] };
        }
      }
    }

    return { allow: false, prevElement: null, transition: null };
  };

  const handleSetPlacement = (newPlacement: ElementPlacement) => {
    setPlacement(newPlacement);
  };

  const handleSetTrimming = (value: boolean) => {
    setTrimming(value);
  };

  return (
    <Fragment>
      <ElementContextOptions
        contextMenuPosition={contextMenuPosition}
        contextMenuVisible={contextMenuVisible}
        contextMenuRef={contextMenuRef}
        currentElement={currentElement}
        onSetContextMenuVisible={handleSetContextMenuVisible}
        preview={preview}
      />
      <DraggableElement
        barRef={barRef}
        currentElement={currentElement}
        onApplyPlacement={applyPlacement}
        onContextMenu={handleContextMenu}
        onSetGapPlacement={setGapPlacement}
        onSetPlacement={handleSetPlacement}
        onSetTranslateX={setTranslateX}
        placement={placement}
        preview={preview}
        translateX={translateX}
        trimming={trimming}
        withVideos={withVideos}
      />
      <ResizeHandlers
        barRef={barRef}
        currentElement={currentElement}
        onApplyPlacement={applyPlacement}
        onSetGapPlacement={setGapPlacement}
        onSetPlacement={handleSetPlacement}
        onSetTranslateX={setTranslateX}
        onSetTrimming={handleSetTrimming}
        placement={placement}
        preview={preview}
      />
    </Fragment>
  );
};

export default Element;
